/**
 * La lecture avec forme de l'acte ouvert, telle que la reçoit l'agent par
 * `canvas_inspect` `formatReading` : le texte de `lireAvecForme`
 * (`@hector/docx-forme`) et ses compteurs, au format du reçu partagé
 * (`CanvasFormatReadingSchema`).
 *
 * WHY à côté de `formePourLAgent.ts` et non à sa place : l'agent lit un modèle
 * d'avocat par `read_document_format`, qui rend ce même texte. Pour reprendre
 * la forme du modèle, il met les deux lectures en regard, ligne à ligne ; le
 * JSON de `formatOf` l'obligeait à traduire avant de comparer (mesuré le
 * 2026-10-11 sur quatre passages : il voyait les écarts d'espacement et de
 * retrait sans les poser). `formatOf` reste la lecture bornée d'une cible, avec
 * l'origine de chaque réglage.
 */

import type { CanvasFormatReading, CanvasFormatReadingRequest } from '@hector/shared-types';
import { lireAvecForme } from '@hector/docx-forme';

/** La lecture rendue à l'agent, et ce qu'elle lui permet de réécrire. */
export interface LectureAvecFormeDeLActe {
  lecture: CanvasFormatReading;
  /**
   * Les identifiants que la page montre sur un paragraphe du corps, dans l'ordre : ceux qu'une écriture de texte
   * adresse. Ils ne partent pas dans `lecture` (le texte les porte déjà) : l'inspection les confronte à ce que
   * l'éditeur publie, puis les remet au courtier (`formatReadingScope`, `superDocCanvasExecutor.ts`).
   */
  identifiantsDuCorps: string[];
}

/**
 * @param octets Le fichier Word de l'acte, exporté en mémoire par l'éditeur.
 * @param demande Le rang du premier paragraphe à rendre.
 * @throws {RangHorsDuDocument} si ce rang n'est pas un paragraphe de l'acte.
 * @throws {LectureDeFormeImpossible} si les octets ne sont pas un fichier Word lisible.
 */
export async function lectureAvecFormePourLAgent(octets: Uint8Array, demande: CanvasFormatReadingRequest): Promise<LectureAvecFormeDeLActe> {
  // Les identifiants : c'est ce qui distingue cette lecture de celle d'un modèle, l'agent écrit sur l'acte.
  const page = await lireAvecForme(octets, { depuis: demande.start_paragraph, identifiants: true });
  return {
    lecture: {
      format_reading: page.texte,
      total_paragraphs: page.totalParagraphes,
      start_paragraph: page.premier,
      end_paragraph: page.dernier,
      next_start_paragraph: page.suivant,
    },
    identifiantsDuCorps: page.identifiantsDuCorps,
  };
}
